import React, { useMemo, useState } from "react";
import { Col, Input, Row } from "reactstrap";

// Circular on purpose and safe: settingsNavDataCompany imports this component
// to put it in its content list, and this reads its gate helper. The call is
// inside the component, not at module scope, so by the time it runs the other
// module has finished evaluating. Do not move it to the top level.
import { isSettingsEntryVisible } from "../../settingsNavDataCompany";

import "./companySettingsIntro.scss";

/**
 * Company Settings landing page, /settings/1/0 (Task 24762).
 *
 * It used to be a centred paragraph: "Welcome to the hub of company
 * configuration..." - which told you nothing and did nothing. There are over
 * fifty settings panels behind the rail, and the landing page was the one
 * place that could have helped you find the right one.
 *
 * So it is now a searchable, grouped set of tiles. Every tile jumps straight
 * to its panel via setActiveTab, the same mechanism the Billing overview
 * (/settings/4/0) already uses.
 *
 * WHY GROUPS AND NOT ONE ALPHABETICAL LIST: fifty-odd entries sorted A-Z reads
 * as a wall. People arrive here knowing the AREA they want to change ("something
 * about invoicing") rather than the exact panel name, so the groups match how
 * the question is asked. Search is there for when they do know the name.
 *
 * The ids are the panel ids from settingsNavDataCompany. They are stable, and
 * an id here with no matching panel simply does nothing - see the note by
 * GROUPS below.
 */

// Panel id -> one plain sentence saying what you would come here to change.
// The rail only has a title, which for "General" or "Company" says nothing at
// all about what is inside.
const GROUPS = [
  {
    title: "Sales & customers",
    icon: "ri-user-star-line",
    accent: "indigo",
    items: [
      { id: 1, title: "Customer", icon: "fa-solid fa-building-user", blurb: "Customer types, statuses and acquisition channels." },
      { id: 35, title: "Leads", icon: "fa-solid fa-filter", blurb: "Lead types, statuses and priorities." },
      { id: 5, title: "Opportunity", icon: "fa-solid fa-chart-line", blurb: "Opportunity stages, types and priorities." },
      { id: 6, title: "Quoting", icon: "fas fa-file-alt", blurb: "Quote statuses and quote defaults." },
      { id: 32, title: "Sales Order", icon: "fa-solid fa-cart-shopping", blurb: "Sales order statuses and behaviour." },
      { id: 49, title: "Customer Portal", icon: "fa-solid fa-globe", blurb: "What customers can see and do in their portal." },
      { id: 37, title: "Loyalty", icon: "fa-solid fa-gift", blurb: "Loyalty scheme, points and rewards." },
    ],
  },
  {
    title: "Work & scheduling",
    icon: "ri-calendar-check-line",
    accent: "sky",
    items: [
      { id: 2, title: "Job", icon: "fa-solid fa-clipboard-list", blurb: "Job types, statuses, priorities and defaults." },
      { id: 3, title: "Task", icon: "fa-solid fa-list-check", blurb: "Task types, statuses and priorities." },
      { id: 45, title: "Task Checklist", icon: "fa-solid fa-clipboard-check", blurb: "Reusable checklist templates for tasks." },
      { id: 23, title: "Appointments", icon: "fa-solid fa-calendar-day", blurb: "Appointment types and statuses." },
      { id: 9, title: "Time Entry", icon: "fa-solid fa-stopwatch", blurb: "Time entry rules and unbillable reasons." },
      { id: 50, title: "SLAs", icon: "fa-solid fa-gauge-high", blurb: "Response and resolution targets." },
      { id: 51, title: "Site Locations", icon: "fa-solid fa-warehouse", blurb: "The addresses work actually happens at." },
    ],
  },
  {
    title: "Money",
    icon: "ri-money-pound-circle-line",
    accent: "emerald",
    items: [
      { id: 4, title: "Invoicing", icon: "fa-solid fa-file-pdf", blurb: "Invoice numbering, statuses and payment terms." },
      { id: 28, title: "Proforma Invoicing", icon: "fa-solid fa-file-invoice", blurb: "The same, for proformas." },
      { id: 7, title: "Banking", icon: "fa-solid fa-building-columns", blurb: "Bank accounts, feeds and clearing rules." },
      { id: 8, title: "Purchases", icon: "fa-solid fa-basket-shopping", blurb: "Purchase categories and statuses." },
      { id: 33, title: "Purchase Order", icon: "fa-solid fa-file-circle-check", blurb: "Purchase order statuses and approvals." },
      { id: 19, title: "Liability", icon: "fa-solid fa-scale-unbalanced", blurb: "Liability providers, categories and statuses." },
      { id: 10, title: "Debt Chasing", icon: "fa-solid fa-hand-holding-dollar", blurb: "Dunning profiles and chase steps." },
      { id: 24, title: "Accounting Periods", icon: "fa-solid fa-calendar-days", blurb: "VAT periods and year ends." },
    ],
  },
  {
    title: "People",
    icon: "ri-team-line",
    accent: "violet",
    items: [
      { id: 11, title: "Staff", icon: "fa-solid fa-people-group", blurb: "Skill sets, certifications and staff defaults." },
      { id: 29, title: "Staff Leave", icon: "fa-solid fa-umbrella-beach", blurb: "Leave types, allowances and restricted periods." },
      { id: 53, title: "Clock-In Kiosks", icon: "fa-solid fa-tablet-screen-button", blurb: "Tablets staff clock in and out on, and their setup links." },
      { id: 30, title: "Organisation Department", icon: "fa-solid fa-sitemap", blurb: "Departments and who sits in them." },
      { id: 17, title: "Team Members", icon: "fa-solid fa-users", blurb: "Who has a login, and what they can reach." },
      { id: 16, title: "User Invitation", icon: "fa-solid fa-envelope-open-text", blurb: "Invite someone new into the company.", gate: "administrator100" },
      { id: 20, title: "Security", icon: "fa-solid fa-shield-halved", blurb: "Roles and what each one is allowed to do." },
    ],
  },
  {
    title: "Products & stock",
    icon: "ri-box-3-line",
    accent: "amber",
    items: [
      { id: 31, title: "Product", icon: "fa-solid fa-tags", blurb: "Products, categories, attributes and pricing." },
      { id: 40, title: "Asset", icon: "fa-solid fa-boxes-stacked", blurb: "Asset types, statuses and attributes." },
      { id: 26, title: "Vehicle", icon: "fa-solid fa-car", blurb: "Customer vehicle types and statuses." },
      { id: 42, title: "Company Vehicle", icon: "fa-solid fa-truck", blurb: "Your own fleet, checks and trackers." },
      { id: 46, title: "POS Hardware", icon: "fa-solid fa-cash-register", blurb: "Card readers, printers and terminals." },
      { id: 38, title: "System Device", icon: "fa-solid fa-microchip", blurb: "Devices registered to this company." },
    ],
  },
  {
    title: "Communication",
    icon: "ri-mail-send-line",
    accent: "rose",
    items: [
      { id: 12, title: "Communications", icon: "fa-solid fa-comments", blurb: "Templates and channels for what you send out." },
      { id: 22, title: "Email", icon: "fa-solid fa-envelope", blurb: "Sending accounts, domains and inbound rules." },
      { id: 39, title: "SMS", icon: "fa-solid fa-message", blurb: "SMS sender and templates." },
      { id: 36, title: "Calls", icon: "fa-solid fa-phone", blurb: "Call providers, numbers and recording." },
      { id: 34, title: "Social Media Management", icon: "fa-solid fa-hashtag", blurb: "Connected social profiles and posting." },
      { id: 48, title: "E-Sign Reminders", icon: "fa-solid fa-file-signature", blurb: "When to chase an unsigned document." },
      { id: 52, title: "Forms", icon: "fa-solid fa-clipboard-list", blurb: "Build and publish forms." },
    ],
  },
  {
    title: "Hospitality",
    icon: "ri-restaurant-line",
    accent: "teal",
    items: [
      { id: 25, title: "Food Preorder", icon: "fa-solid fa-utensils", blurb: "Pre-order events, menus and guest settings." },
      { id: 13, title: "Menus", icon: "fa-solid fa-book-open", blurb: "Food menus, categories and items." },
    ],
  },
  {
    title: "This company",
    icon: "ri-settings-3-line",
    accent: "slate",
    items: [
      { id: 21, title: "Company", icon: "fa-solid fa-building", blurb: "Name, address, logo and registration details." },
      { id: 15, title: "General", icon: "fa-solid fa-sliders", blurb: "Company-wide defaults that do not belong anywhere else." },
      { id: 43, title: "Config Items", icon: "fa-solid fa-toggle-on", blurb: "Individual feature switches." },
      { id: 41, title: "Attachments", icon: "fa-solid fa-paperclip", blurb: "Where files are stored and what is allowed." },
      { id: 27, title: "3rd Party Integration", icon: "fa-solid fa-plug", blurb: "Xero, HMRC, Companies House, BrightPay, GPSLive and the rest." },
      { id: 44, title: "API Keys", icon: "fa-solid fa-key", blurb: "Keys for anything talking to CRMit from outside." },
      { id: 47, title: "Onboarding", icon: "fa-solid fa-rocket", blurb: "Onboarding checklists for new customers." },
      { id: 18, title: "Manage License", icon: "fa-solid fa-id-card", blurb: "Licences in use and who holds them." },
    ],
  },
];

export default function CompanySettingsIntro({ setActiveTab }) {
  const [query, setQuery] = useState("");

  const groups = useMemo(() => {
    const term = query.trim().toLowerCase();
    return GROUPS.map((group) => ({
      ...group,
      items: group.items.filter((item) => {
        // A gated entry is hidden here exactly as it is in the rail. Offering
        // a tile that lands on "you don't have access" is worse than not
        // offering it.
        if (item.gate && !isSettingsEntryVisible({ gate: item.gate })) {
          return false;
        }
        if (!term) return true;
        return (
          item.title.toLowerCase().includes(term) ||
          item.blurb.toLowerCase().includes(term) ||
          group.title.toLowerCase().includes(term)
        );
      }),
    })).filter((group) => group.items.length > 0);
  }, [query]);

  const totalShown = groups.reduce((sum, g) => sum + g.items.length, 0);

  return (
    <div className="company-settings-intro">
      <div className="company-settings-intro__hero">
        <div>
          <h4 className="mb-1">Company Settings</h4>
          <p className="text-muted mb-0">
            Everything that decides how CRMit behaves for this company. Pick an
            area below, or search if you already know what you are after.
          </p>
        </div>
        <div className="search-box company-settings-intro__search">
          <Input
            type="search"
            className="form-control search"
            placeholder="Search settings..."
            value={query}
            onChange={(e) => setQuery(e.target.value)}
          />
          <i className="ri-search-line search-icon"></i>
        </div>
      </div>

      {totalShown === 0 && (
        <div className="text-center text-muted py-5">
          <i className="ri-search-eye-line d-block fs-24 mb-2"></i>
          Nothing matches "{query}". The full list is always in the rail on the
          left.
        </div>
      )}

      {groups.map((group) => (
        <div key={group.title} className="company-settings-intro__group">
          <div className="company-settings-intro__group-title">
            <i className={group.icon}></i>
            <span>{group.title}</span>
          </div>

          <Row className="g-2">
            {group.items.map((item) => (
              <Col key={item.id} xs={12} sm={6} xl={4}>
                <button
                  type="button"
                  className={`company-settings-intro__tile company-settings-intro__tile--${group.accent}`}
                  // Every panel is reachable from the rail too, so a missing
                  // setActiveTab (this component rendered somewhere else)
                  // degrades to a tile that simply does nothing rather than
                  // throwing.
                  onClick={() => setActiveTab?.(item.id)}
                >
                  <i className={`${item.icon} company-settings-intro__tile-icon`}></i>
                  <span className="company-settings-intro__tile-body">
                    <span className="company-settings-intro__tile-title">
                      {item.title}
                    </span>
                    <span className="company-settings-intro__tile-blurb">
                      {item.blurb}
                    </span>
                  </span>
                  <i className="ri-arrow-right-s-line company-settings-intro__tile-chevron"></i>
                </button>
              </Col>
            ))}
          </Row>
        </div>
      ))}

      <div className="text-muted fs-13 mt-4">
        Cannot find it? Everything is also listed in the rail on the left, and{" "}
        <button
          type="button"
          className="btn btn-link p-0 align-baseline"
          onClick={() => window.Tawk_API?.toggle?.()}
        >
          chat to us
        </button>{" "}
        if you are stuck.
      </div>
    </div>
  );
}
